<template>
  <div class="flex flex-col md:h-full">
    <!-- `sticky top-0` pins the video while the content below scrolls past
    it — mobile only (`md:static` opts desktop out, since desktop's whole
    column already scrolls as one block). No horizontal/vertical padding
    here — the video stays edge-to-edge against the panel/drawer width.
    `rounded` defaults to true (mobile: matches RevDrawer's own rounded
    top-corner sheet). Desktop passes `false` — its panel container already
    clips corners via `overflow-hidden rounded-lg`, and rounding the video's
    bottom corners too (which sit mid-panel, not on a panel edge) revealed an
    arc of the panel background peeking through where the video meets the
    content below.

    No gap between the video and the scrollable div below (unlike the two
    children inside that div, which do use `gap-16`) — a flex `gap` here
    would sit *outside* the scroll container, so it wouldn't scroll away and
    would stay visible as a static seam under the video even once scrolled
    past it. The scrollable div's own `p-16` (below) gives the same initial
    breathing room but scrolls away with the rest of the content instead. -->
    <LiveShoppingVideo
      ref="videoRef"
      class="sticky top-0 z-10 md:static"
      :muted="false"
      :rounded
      :title
      :video-id
      @ended="emit('video-progress', 100)"
      @progress="(percent) => emit('video-progress', percent)"
      @start="emit('video-start')"
    />

    <!-- Padding lives here (not on the video, not on the outer scroll
    container in LiveShoppingWidget.vue) — `p-16` on all sides so the top and
    bottom of the list aren't visually cropped by the scroll boundary as soon
    as the view is scrolled even slightly. -->
    <div class="flex flex-col gap-16 p-16 md:flex-1 md:overflow-y-auto">
      <span class="flex items-center justify-between gap-8">
        <RevTag
          :label="i18n(translations.tagLabel)"
          size="small"
          variant="danger"
          variation="filled"
        />
        <span class="caption text-static-default-low">
          {{ productCountLabel }}
        </span>
      </span>

      <RevInfoBlock
        v-if="status === 'error'"
        :content="i18n(translations.errorMessage)"
        :icon="IconWarning"
        :title="i18n(translations.errorMessage)"
        variant="warning"
      />

      <template v-else-if="status === 'pending'">
        <RevSkeleton height="104px" shape="rectangle" />
        <RevSkeleton height="104px" shape="rectangle" />
      </template>

      <template v-else>
        <LiveShoppingProductCard
          v-for="(product, index) in products"
          :key="product.objectId"
          :product
          @add-to-cart="emit('add-to-cart', product, index)"
          @jump-to-moment="emit('jump-to-moment', product, index)"
          @product-click="emit('product-click', product, index)"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevTag } from '@ds/components/Tag'
import { IconWarning } from '@ds/icons/IconWarning'

import type { LiveShoppingProduct } from '../composables/useLiveShoppingProducts'

import LiveShoppingProductCard from './LiveShoppingProductCard.vue'
import LiveShoppingVideo from './LiveShoppingVideo.vue'
import translations from './LiveShoppingWidget.translations'

const { rounded = true } = defineProps<{
  /**
   * Pre-translated by the widget per category ("5 iPads" / "4 MacBooks") —
   * the children don't know the category, only how to display the label.
   */
  productCountLabel: string
  products: LiveShoppingProduct[]
  /**
   * Passed through to the video. Desktop passes `false` (its panel already
   * clips corners via `overflow-hidden rounded-lg`); mobile keeps the
   * default `true` to match RevDrawer's own rounded sheet top.
   */
  rounded?: boolean
  status: 'idle' | 'pending' | 'success' | 'error'
  title: string
  videoId: string
}>()

const emit = defineEmits<{
  (e: 'video-start'): void
  (e: 'video-progress', percent: number): void
  (e: 'jump-to-moment', product: LiveShoppingProduct, position: number): void
  (e: 'product-click', product: LiveShoppingProduct, position: number): void
  (e: 'add-to-cart', product: LiveShoppingProduct, position: number): void
}>()

const i18n = useI18n()

const videoRef = useTemplateRef('videoRef')

function seekTo(seconds: number) {
  videoRef.value?.seekTo(seconds)
}

function pause() {
  return videoRef.value?.pause() ?? false
}

function resume() {
  videoRef.value?.resume()
}

defineExpose({ seekTo, pause, resume })
</script>
